import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import shp from 'shpjs';
// @ts-ignore
import * as toGeoJSON from '@mapbox/togeojson';
import { LayoutResponse, SiteBBox, CableSegment } from '../types';

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';

// Pixel radius within which clicking snaps to first vertex to close polygon
const CLOSE_SNAP_PX = 14;

// ── Exclusion zone types & colours ───────────────────────────────────────────

export type ExclusionZoneType = 'heritage' | 'setback' | 'watercourse' | 'slope' | 'custom';

export interface ExclusionZone {
  id:          string;
  zone_type:   ExclusionZoneType;
  label?:      string;
  coordinates: [number, number][][];
}

const ZONE_COLORS: Record<ExclusionZoneType, string> = {
  heritage:    '#E74C3C',
  setback:     '#F39C12',
  watercourse: '#3498DB',
  slope:       '#A04000',
  custom:      '#8E44AD',
};

const ZONE_LABELS: Record<ExclusionZoneType, string> = {
  heritage:    '🏛 Heritage',
  setback:     '📏 Setback',
  watercourse: '💧 Watercourse',
  slope:       '⛰ Slope',
  custom:      '✏️ Custom',
};

// ── Measurement tool helpers (module scope) ──────────────────────────────────

const _SETBACK_M = 10; // default boundary setback used in net-area estimate

function _haversine(a: [number, number], b: [number, number]): number {
  const R  = 6_371_000;
  const φ1 = a[1] * Math.PI / 180, φ2 = b[1] * Math.PI / 180;
  const Δφ = (b[1] - a[1]) * Math.PI / 180;
  const Δλ = (b[0] - a[0]) * Math.PI / 180;
  const x  = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

function _fmtDist(m: number): string {
  return m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${Math.round(m)} m`;
}

function _calcAreaHa(pts: [number, number][]): number {
  if (pts.length < 3) return 0;
  const midLat  = pts.reduce((s, p) => s + p[1], 0) / pts.length;
  const mPerLng = 111_320 * Math.cos(midLat * Math.PI / 180);
  const mPerLat = 110_540;
  let   area    = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    area += (pts[i][0] * mPerLng) * (pts[j][1] * mPerLat);
    area -= (pts[j][0] * mPerLng) * (pts[i][1] * mPerLat);
  }
  return Math.abs(area) / 2 / 10_000;
}

// Net area after uniform inward setback using perimeter-area formula
// A_net ≈ A_gross − perimeter × setback + π × setback²  (exact for convex, good approximation for irregular)
function _calcNetAreaHa(pts: [number, number][], setbackM: number): number {
  if (pts.length < 3) return 0;
  const grossHa = _calcAreaHa(pts);
  let   perimM  = 0;
  for (let i = 0; i < pts.length; i++) perimM += _haversine(pts[i], pts[(i + 1) % pts.length]);
  const netM2 = Math.max(0, grossHa * 10_000 - perimM * setbackM + Math.PI * setbackM * setbackM);
  return netM2 / 10_000;
}

function _updateMeasureOverlay(
  map:    mapboxgl.Map,
  points: [number, number][],
  closed: boolean,
): void {
  const lineCoords: [number, number][] = [...points];
  if (closed && points.length >= 3) lineCoords.push(points[0]);

  const labelFeatures: GeoJSON.Feature[] = [];
  let totalDist = 0;

  for (let i = 0; i < lineCoords.length - 1; i++) {
    const a = lineCoords[i], b = lineCoords[i + 1];
    const d = _haversine(a, b);
    totalDist += d;
    const mid: [number, number] = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    labelFeatures.push({
      type: 'Feature', properties: { label: _fmtDist(d) },
      geometry: { type: 'Point', coordinates: mid },
    });
  }

  if (points.length >= 2) {
    const last = points[points.length - 1];
    let summaryLabel = `Total: ${_fmtDist(totalDist)}`;
    if (closed && points.length >= 3) {
      const grossHa = _calcAreaHa(points);
      const netHa   = _calcNetAreaHa(points, _SETBACK_M);
      summaryLabel  = `Total: ${_fmtDist(totalDist)} | Gross: ${grossHa.toFixed(2)} ha | Net (−${_SETBACK_M}m): ${netHa.toFixed(2)} ha`;
    }
    labelFeatures.push({
      type: 'Feature',
      properties: { label: summaryLabel },
      geometry: { type: 'Point', coordinates: [last[0], last[1] - 0.0004] },
    });
  }

  const src = (id: string) => map.getSource(id) as mapboxgl.GeoJSONSource | undefined;

  src('measure-points')?.setData({
    type: 'FeatureCollection',
    features: points.map(p => ({
      type: 'Feature' as const, properties: {},
      geometry: { type: 'Point' as const, coordinates: p },
    })),
  });
  src('measure-line')?.setData({
    type: 'FeatureCollection',
    features: lineCoords.length >= 2
      ? [{ type: 'Feature' as const, properties: {},
           geometry: { type: 'LineString' as const, coordinates: lineCoords } }]
      : [],
  });
  src('measure-fill')?.setData({
    type: 'FeatureCollection',
    features: closed && points.length >= 3
      ? [{ type: 'Feature' as const, properties: {},
           geometry: { type: 'Polygon' as const, coordinates: [[...points, points[0]]] } }]
      : [],
  });
  src('measure-labels')?.setData({ type: 'FeatureCollection', features: labelFeatures });
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  response:             LayoutResponse | null;
  siteBox:              SiteBBox | null;
  exclusionZones:       ExclusionZone[];
  onSiteDrawn:          (box: SiteBBox) => void;
  onSitePolygonDrawn?:  (poly: number[][]) => void;
  onClearSite:          () => void;
  onExclusionZoneAdded: (zone: ExclusionZone) => void;
  onExclusionZoneClear: () => void;
}

type DrawMode = 'site' | 'exclusion';

interface LayerToggles {
  terrain:   boolean;
  roads:     boolean;
  labels:    boolean;
  hillshade: boolean;
}

// ── Coordinate helpers ────────────────────────────────────────────────────────

function metresToLngLat(x: number, y: number, swLng: number, swLat: number): [number, number] {
  const latRad = swLat * (Math.PI / 180);
  return [swLng + x / (111_320 * Math.cos(latRad)), swLat + y / 110_540];
}

function rectToPolygon(
  x0: number, y0: number, x1: number, y1: number,
  swLng: number, swLat: number
): GeoJSON.Feature<GeoJSON.Polygon> {
  const sw = metresToLngLat(x0, y0, swLng, swLat);
  const se = metresToLngLat(x1, y0, swLng, swLat);
  const ne = metresToLngLat(x1, y1, swLng, swLat);
  const nw = metresToLngLat(x0, y1, swLng, swLat);
  return {
    type: 'Feature', properties: {},
    geometry: { type: 'Polygon', coordinates: [[sw, se, ne, nw, sw]] },
  };
}

function bboxFromCoords(coords: [number, number][]): SiteBBox {
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  for (const [lng, lat] of coords) {
    if (lng < minLng) minLng = lng;
    if (lat < minLat) minLat = lat;
    if (lng > maxLng) maxLng = lng;
    if (lat > maxLat) maxLat = lat;
  }
  return [minLng, minLat, maxLng, maxLat];
}

function bboxFromFC(fc: GeoJSON.FeatureCollection): SiteBBox {
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  const visit = (coords: any) => {
    if (typeof coords[0] === 'number') {
      if (coords[0] < minLng) minLng = coords[0];
      if (coords[1] < minLat) minLat = coords[1];
      if (coords[0] > maxLng) maxLng = coords[0];
      if (coords[1] > maxLat) maxLat = coords[1];
    } else coords.forEach(visit);
  };
  fc.features.forEach(f => f.geometry && visit((f.geometry as any).coordinates));
  return [minLng, minLat, maxLng, maxLat];
}

function zonesToFC(zones: ExclusionZone[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: zones.map(z => ({
      type: 'Feature' as const,
      properties: { zone_type: z.zone_type, label: z.label || z.zone_type },
      geometry: { type: 'Polygon' as const, coordinates: z.coordinates },
    })),
  };
}

function emptyFC(): GeoJSON.FeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}

function getSource(map: mapboxgl.Map, id: string): mapboxgl.GeoJSONSource | null {
  return (map.getSource(id) as mapboxgl.GeoJSONSource) ?? null;
}

// ── Drawing preview builders ──────────────────────────────────────────────────

function buildPreview(
  verts: [number, number][],
  cursor: [number, number] | null,
  nearClose: boolean,
): GeoJSON.FeatureCollection {
  if (verts.length === 0) return emptyFC();
  const features: GeoJSON.Feature[] = [];

  if (verts.length >= 2) {
    features.push({
      type: 'Feature', properties: { kind: 'edge' },
      geometry: { type: 'LineString', coordinates: verts },
    });
  }
  if (cursor) {
    features.push({
      type: 'Feature', properties: { kind: 'rubber' },
      geometry: { type: 'LineString', coordinates: [verts[verts.length - 1], cursor] },
    });
  }
  if (nearClose && verts.length >= 3 && cursor) {
    features.push({
      type: 'Feature', properties: { kind: 'close' },
      geometry: { type: 'LineString', coordinates: [cursor, verts[0]] },
    });
  }
  return { type: 'FeatureCollection', features };
}

function buildVertexDots(verts: [number, number][], nearClose: boolean): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: verts.map((v, i) => ({
      type: 'Feature' as const,
      properties: { first: i === 0, highlight: i === 0 && nearClose },
      geometry: { type: 'Point' as const, coordinates: v },
    })),
  };
}

function buildFillPreview(verts: [number, number][], cursor: [number, number] | null): GeoJSON.FeatureCollection {
  if (verts.length < 2) return emptyFC();
  const ring = cursor ? [...verts, cursor, verts[0]] : [...verts, verts[0]];
  return {
    type: 'FeatureCollection',
    features: [{ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [ring] } }],
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

export function MapView({
  response, siteBox, exclusionZones,
  onSiteDrawn, onSitePolygonDrawn, onClearSite, onExclusionZoneAdded, onExclusionZoneClear,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef       = useRef<mapboxgl.Map | null>(null);
  const mapLoadedRef = useRef(false);

  // Polygon drawing refs
  const drawingRef    = useRef(false);
  const verticesRef   = useRef<[number, number][]>([]);
  const cursorRef     = useRef<[number, number] | null>(null);
  const nearCloseRef  = useRef(false);
  const drawModeRef   = useRef<DrawMode>('site');
  const zoneTypeRef   = useRef<ExclusionZoneType>('heritage');

  // Callback refs
  const onSiteDrawnRef            = useRef(onSiteDrawn);
  const onSitePolygonDrawnRef     = useRef(onSitePolygonDrawn);
  const onClearSiteRef            = useRef(onClearSite);
  const onExclusionZoneAddedRef   = useRef(onExclusionZoneAdded);
  useEffect(() => { onSiteDrawnRef.current          = onSiteDrawn;          }, [onSiteDrawn]);
  useEffect(() => { onSitePolygonDrawnRef.current   = onSitePolygonDrawn;   }, [onSitePolygonDrawn]);
  useEffect(() => { onClearSiteRef.current          = onClearSite;          }, [onClearSite]);
  useEffect(() => { onExclusionZoneAddedRef.current = onExclusionZoneAdded; }, [onExclusionZoneAdded]);

  const shpInputRef = useRef<HTMLInputElement>(null);
  const kmlInputRef = useRef<HTMLInputElement>(null);
  const dxfInputRef = useRef<HTMLInputElement>(null);

  const [isDrawing, setIsDrawing]         = useState(false);
  const [vertexCount, setVertexCount]     = useState(0);
  const [drawMode, setDrawMode]           = useState<DrawMode>('site');
  const [zoneType, setZoneType]           = useState<ExclusionZoneType>('heritage');
  const [layerPanel, setLayerPanel]       = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError]     = useState<string | null>(null);
  const [layers, setLayers]               = useState<LayerToggles>({
    terrain: false, roads: true, labels: true, hillshade: false,
  });

  // Overlay toggle state
  const [showInverters, setShowInverters] = useState(true);
  const [showDcCables,  setShowDcCables]  = useState(false);
  const [showAcCables,  setShowAcCables]  = useState(false);

  // ── Measurement tool state ───────────────────────────────────────────────
  const [measureMode,   setMeasureMode]   = useState(false);
  const [measurePoints, setMeasurePoints] = useState<[number, number][]>([]);
  const [measureClosed, setMeasureClosed] = useState(false);

  const measureModeRef   = useRef(false);
  const measurePointsRef = useRef<[number, number][]>([]);
  const measureClosedRef = useRef(false);

  useEffect(() => { measureModeRef.current   = measureMode;   }, [measureMode]);
  useEffect(() => { measurePointsRef.current = measurePoints; }, [measurePoints]);
  useEffect(() => { measureClosedRef.current = measureClosed; }, [measureClosed]);

  useEffect(() => { drawModeRef.current = drawMode; }, [drawMode]);
  useEffect(() => { zoneTypeRef.current = zoneType; }, [zoneType]);

  // ── Cancel / finish helpers ───────────────────────────────────────────────

  const cancelDrawing = () => {
    drawingRef.current = false;
    verticesRef.current = [];
    cursorRef.current = null;
    nearCloseRef.current = false;
    setIsDrawing(false);
    setVertexCount(0);
    const map = mapRef.current;
    if (!map) return;
    getSource(map, 'draw-preview')?.setData(emptyFC());
    getSource(map, 'draw-vertices')?.setData(emptyFC());
    getSource(map, 'draw-fill')?.setData(emptyFC());
    map.getCanvas().style.cursor = 'crosshair';
  };

  const finishPolygonRef = useRef<((map: mapboxgl.Map) => void) | null>(null);
  finishPolygonRef.current = (map: mapboxgl.Map) => {
    const verts = verticesRef.current;
    if (verts.length < 3) return;

    const ring: [number, number][] = [...verts, verts[0]];
    const poly: GeoJSON.Feature<GeoJSON.Polygon> = {
      type: 'Feature', properties: {},
      geometry: { type: 'Polygon', coordinates: [ring] },
    };

    const mode = drawModeRef.current;
    const zt   = zoneTypeRef.current;

    getSource(map, 'draw-preview')?.setData(emptyFC());
    getSource(map, 'draw-vertices')?.setData(emptyFC());
    getSource(map, 'draw-fill')?.setData(emptyFC());
    drawingRef.current = false;
    verticesRef.current = [];
    cursorRef.current = null;
    nearCloseRef.current = false;
    setIsDrawing(false);
    setVertexCount(0);
    map.getCanvas().style.cursor = 'crosshair';

    if (mode === 'site') {
      getSource(map, 'site-draw')?.setData({ type: 'FeatureCollection', features: [poly] });
      getSource(map, 'tracker-field')?.setData(emptyFC());
      getSource(map, 'bess')?.setData(emptyFC());
      getSource(map, 'roads')?.setData(emptyFC());
      getSource(map, 'inverters')?.setData(emptyFC());
      getSource(map, 'dc-cables')?.setData(emptyFC());
      getSource(map, 'ac-cables')?.setData(emptyFC());
      onSiteDrawnRef.current(bboxFromCoords(verts));
      // Emit the raw polygon so engine can clip irregular shapes
      if (onSitePolygonDrawnRef.current) {
        onSitePolygonDrawnRef.current(verts.map(v => [v[0], v[1]]));
      }
    } else {
      onExclusionZoneAddedRef.current({
        id: `zone_${Date.now()}`,
        zone_type: zt,
        coordinates: [ring],
      });
      setDrawMode('site');
    }
  };

  // ── Map initialisation ─────────────────────────────────────────────────────
  useEffect(() => {
    if (mapRef.current || !containerRef.current || !TOKEN) return;

    mapboxgl.accessToken = TOKEN;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      center: [115.86, -31.95],
      zoom: 10,
    });
    mapRef.current = map;

    map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    map.addControl(new mapboxgl.ScaleControl({ unit: 'metric' }), 'bottom-right');

    // Escape to cancel
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (measureModeRef.current) {
          // Clear measurement but stay in measure mode
          measurePointsRef.current = [];
          measureClosedRef.current = false;
          setMeasurePoints([]);
          setMeasureClosed(false);
          _updateMeasureOverlay(map, [], false);
          return;
        }
        if (drawingRef.current) cancelDrawing();
      }
    };
    window.addEventListener('keydown', onKeyDown);

    map.on('load', () => {
      mapLoadedRef.current = true;

      map.addSource('mapbox-dem', { type: 'raster-dem', url: 'mapbox://mapbox.mapbox-terrain-dem-v1', tileSize: 512, maxzoom: 14 });
      map.addLayer({ id: 'hillshade-layer', type: 'hillshade', source: 'mapbox-dem', paint: { 'hillshade-exaggeration': 0.5 }, layout: { visibility: 'none' } }, 'waterway-label');

      // Committed exclusion zones
      map.addSource('exclusion-zones', { type: 'geojson', data: emptyFC() });
      map.addLayer({ id: 'exclusion-fill', type: 'fill', source: 'exclusion-zones',
        paint: { 'fill-color': ['match', ['get', 'zone_type'], 'heritage', ZONE_COLORS.heritage, 'setback', ZONE_COLORS.setback, 'watercourse', ZONE_COLORS.watercourse, 'slope', ZONE_COLORS.slope, ZONE_COLORS.custom], 'fill-opacity': 0.30 } });
      map.addLayer({ id: 'exclusion-outline', type: 'line', source: 'exclusion-zones',
        paint: { 'line-color': ['match', ['get', 'zone_type'], 'heritage', ZONE_COLORS.heritage, 'setback', ZONE_COLORS.setback, 'watercourse', ZONE_COLORS.watercourse, 'slope', ZONE_COLORS.slope, ZONE_COLORS.custom], 'line-width': 2 } });

      // Committed site boundary
      map.addSource('site-draw', { type: 'geojson', data: emptyFC() });
      map.addLayer({ id: 'site-fill',    type: 'fill', source: 'site-draw', paint: { 'fill-color': '#2E5FA3', 'fill-opacity': 0.12 } });
      map.addLayer({ id: 'site-outline', type: 'line', source: 'site-draw', paint: { 'line-color': '#2E5FA3', 'line-width': 2, 'line-dasharray': [4, 2] } });

      // Tracker field extent — low-zoom overview
      map.addSource('tracker-field', { type: 'geojson', data: emptyFC() });
      map.addLayer({ id: 'tracker-fill',    type: 'fill', source: 'tracker-field', paint: { 'fill-color': '#17607A', 'fill-opacity': 0.06 }, maxzoom: 13 });
      map.addLayer({ id: 'tracker-outline', type: 'line', source: 'tracker-field', paint: { 'line-color': '#17607A', 'line-width': 1.5 } });

      // Individual tracker cells — high-zoom detail
      map.addSource('tracker-rows', { type: 'geojson', data: emptyFC() });
      map.addLayer({ id: 'tracker-rows-fill',    type: 'fill', source: 'tracker-rows', paint: { 'fill-color': '#17607A', 'fill-opacity': 0.55 }, minzoom: 13 });
      map.addLayer({ id: 'tracker-rows-outline', type: 'line', source: 'tracker-rows', paint: { 'line-color': '#0e4a5c', 'line-width': 0.4 }, minzoom: 13 });

      // BESS compound
      map.addSource('bess', { type: 'geojson', data: emptyFC() });
      map.addLayer({ id: 'bess-fill', type: 'fill', source: 'bess', paint: { 'fill-color': '#C55A11', 'fill-opacity': 0.6 } });

      // ── Roads (spine + cross) ────────────────────────────────────────────────
      map.addSource('roads', { type: 'geojson', data: emptyFC() });
      map.addLayer({ id: 'roads-fill',    type: 'fill', source: 'roads',
        paint: { 'fill-color': '#c4a35a', 'fill-opacity': 0.70 }, minzoom: 13 });
      map.addLayer({ id: 'roads-outline', type: 'line', source: 'roads',
        paint: { 'line-color': '#9e7d3a', 'line-width': 0.5 }, minzoom: 13 });

      // ── Inverter footprints ───────────────────────────────────────────────
      map.addSource('inverters', { type: 'geojson', data: emptyFC() });
      map.addLayer({ id: 'inverters-fill',    type: 'fill', source: 'inverters',
        paint: { 'fill-color': '#C55A11', 'fill-opacity': 0.85 } });
      map.addLayer({ id: 'inverters-outline', type: 'line', source: 'inverters',
        paint: { 'line-color': '#7a3509', 'line-width': 1.5 } });
      map.addLayer({ id: 'inverters-label', type: 'symbol', source: 'inverters',
        layout: {
          'text-field':       ['get', 'label'],
          'text-size':        ['interpolate', ['linear'], ['zoom'], 13, 8, 16, 11],
          'text-max-width':   6,
          'text-anchor':      'center',
          'text-allow-overlap': false,
        },
        paint: {
          'text-color':            '#fff',
          'text-halo-color':       '#7a3509',
          'text-halo-width':       1.5,
        },
        minzoom: 12,
      });

      // ── DC home-run cables ────────────────────────────────────────────────
      map.addSource('dc-cables', { type: 'geojson', data: emptyFC() });
      map.addLayer({ id: 'dc-cables-line', type: 'line', source: 'dc-cables',
        paint: { 'line-color': '#f97316', 'line-width': 1.5, 'line-dasharray': [4, 2] } });

      // ── AC MV cables ──────────────────────────────────────────────────────
      map.addSource('ac-cables', { type: 'geojson', data: emptyFC() });
      map.addLayer({ id: 'ac-cables-line', type: 'line', source: 'ac-cables',
        paint: { 'line-color': '#dc2626', 'line-width': 2 } });

      // ── Draw preview layers — added LAST so they always render above tracker cells ──
      map.addSource('draw-fill', { type: 'geojson', data: emptyFC() });
      map.addLayer({ id: 'draw-fill-layer', type: 'fill', source: 'draw-fill',
        paint: { 'fill-color': '#2E5FA3', 'fill-opacity': 0.08 } });

      map.addSource('draw-preview', { type: 'geojson', data: emptyFC() });
      // White halo behind the draw line so it's visible over dark tracker cells
      map.addLayer({ id: 'draw-edge-halo', type: 'line', source: 'draw-preview',
        filter: ['==', ['get', 'kind'], 'edge'],
        paint: { 'line-color': '#ffffff', 'line-width': 3.5 } });
      map.addLayer({ id: 'draw-edge', type: 'line', source: 'draw-preview',
        filter: ['==', ['get', 'kind'], 'edge'],
        paint: { 'line-color': '#2E5FA3', 'line-width': 2 } });
      map.addLayer({ id: 'draw-rubber', type: 'line', source: 'draw-preview',
        filter: ['in', ['get', 'kind'], ['literal', ['rubber', 'close']]],
        paint: { 'line-color': '#2E5FA3', 'line-width': 1.5, 'line-dasharray': [4, 3] } });

      map.addSource('draw-vertices', { type: 'geojson', data: emptyFC() });
      map.addLayer({ id: 'draw-vertex-dot', type: 'circle', source: 'draw-vertices',
        paint: {
          'circle-radius':       ['case', ['get', 'highlight'], 10, ['get', 'first'], 7, 5],
          'circle-color':        ['case', ['get', 'highlight'], '#27AE60', ['get', 'first'], '#F39C12', '#2E5FA3'],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#fff',
        },
      });

      // ── Measurement overlay — topmost, added last ─────────────────────────
      map.addSource('measure-fill',   { type: 'geojson', data: emptyFC() });
      map.addSource('measure-line',   { type: 'geojson', data: emptyFC() });
      map.addSource('measure-points', { type: 'geojson', data: emptyFC() });
      map.addSource('measure-labels', { type: 'geojson', data: emptyFC() });

      map.addLayer({ id: 'measure-fill-layer', type: 'fill', source: 'measure-fill',
        paint: { 'fill-color': '#f59e0b', 'fill-opacity': 0.10 } });
      map.addLayer({ id: 'measure-line-layer', type: 'line', source: 'measure-line',
        paint: { 'line-color': '#f59e0b', 'line-width': 2.5, 'line-dasharray': [4, 2] } });
      map.addLayer({ id: 'measure-points-layer', type: 'circle', source: 'measure-points',
        paint: {
          'circle-radius':       5,
          'circle-color':        '#f59e0b',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#fff',
        },
      });
      map.addLayer({ id: 'measure-labels-layer', type: 'symbol', source: 'measure-labels',
        layout: {
          'text-field':            ['get', 'label'],
          'text-size':             12,
          'text-anchor':           'center',
          'text-offset':           [0, -1.5],
          'text-allow-overlap':    true,
          'text-ignore-placement': true,
        },
        paint: {
          'text-color':       '#92400e',
          'text-halo-color':  '#ffffff',
          'text-halo-width':  2,
        },
      });
    });

    map.getCanvas().style.cursor = 'crosshair';

    // ── Click ────────────────────────────────────────────────────────────────
    map.on('click', (e) => {
      const pt: [number, number] = [e.lngLat.lng, e.lngLat.lat];

      // Measurement mode intercepts all clicks
      if (measureModeRef.current && !measureClosedRef.current) {
        const updated = [...measurePointsRef.current, pt];
        measurePointsRef.current = updated;
        setMeasurePoints(updated);
        _updateMeasureOverlay(map, updated, false);
        return;
      }

      if (!drawingRef.current) {
        // Start new polygon
        drawingRef.current = true;
        verticesRef.current = [pt];
        setIsDrawing(true);
        setVertexCount(1);
        if (drawModeRef.current === 'site') {
          getSource(map, 'site-draw')?.setData(emptyFC());
          getSource(map, 'tracker-field')?.setData(emptyFC());
          getSource(map, 'bess')?.setData(emptyFC());
          onClearSiteRef.current();
        }
        getSource(map, 'draw-vertices')?.setData(buildVertexDots([pt], false));
      } else {
        // Close if near first vertex
        if (verticesRef.current.length >= 3 && nearCloseRef.current) {
          finishPolygonRef.current!(map);
          return;
        }
        // Add vertex
        verticesRef.current = [...verticesRef.current, pt];
        setVertexCount(verticesRef.current.length);
        getSource(map, 'draw-vertices')?.setData(buildVertexDots(verticesRef.current, nearCloseRef.current));
        getSource(map, 'draw-preview')?.setData(buildPreview(verticesRef.current, cursorRef.current, nearCloseRef.current));
        getSource(map, 'draw-fill')?.setData(buildFillPreview(verticesRef.current, cursorRef.current));
      }
    });

    // ── Double-click: close ───────────────────────────────────────────────
    map.on('dblclick', (e) => {
      e.preventDefault();
      // Measurement: double-click closes the polygon and shows area
      if (measureModeRef.current && !measureClosedRef.current && measurePointsRef.current.length >= 3) {
        // Remove the duplicate point added by the preceding single click
        const pts = measurePointsRef.current.slice(0, -1);
        measurePointsRef.current = pts;
        measureClosedRef.current = true;
        setMeasurePoints(pts);
        setMeasureClosed(true);
        _updateMeasureOverlay(map, pts, true);
        return;
      }
      if (!drawingRef.current || verticesRef.current.length < 3) return;
      // Remove extra vertex added by first click of dblclick
      verticesRef.current = verticesRef.current.slice(0, -1);
      finishPolygonRef.current!(map);
    });

    // ── Mousemove ─────────────────────────────────────────────────────────
    map.on('mousemove', (e) => {
      if (!drawingRef.current || verticesRef.current.length === 0) return;
      const cur: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      cursorRef.current = cur;

      // Near-close detection
      let nearClose = false;
      if (verticesRef.current.length >= 3) {
        const fp = map.project(verticesRef.current[0] as mapboxgl.LngLatLike);
        const cp = map.project(cur as mapboxgl.LngLatLike);
        const dx = fp.x - cp.x, dy = fp.y - cp.y;
        nearClose = Math.sqrt(dx * dx + dy * dy) < CLOSE_SNAP_PX;
      }
      nearCloseRef.current = nearClose;
      map.getCanvas().style.cursor = nearClose ? 'pointer' : 'crosshair';

      getSource(map, 'draw-preview')?.setData(buildPreview(verticesRef.current, cur, nearClose));
      getSource(map, 'draw-vertices')?.setData(buildVertexDots(verticesRef.current, nearClose));
      getSource(map, 'draw-fill')?.setData(buildFillPreview(verticesRef.current, cur));
    });

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      mapLoadedRef.current = false;
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Sync exclusion zones ──────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoadedRef.current) return;
    getSource(map, 'exclusion-zones')?.setData(zonesToFC(exclusionZones));
  }, [exclusionZones]);

  // ── Layer toggles ─────────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      if (layers.terrain) { map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 }); }
      else { (map as any).setTerrain(null); }
      if (map.getLayer('hillshade-layer'))
        map.setLayoutProperty('hillshade-layer', 'visibility', layers.hillshade ? 'visible' : 'none');
      const roadPfx = ['road', 'bridge', 'tunnel'];
      map.getStyle()?.layers?.forEach(l => {
        if (roadPfx.some(p => l.id.toLowerCase().startsWith(p)))
          map.setLayoutProperty(l.id, 'visibility', layers.roads ? 'visible' : 'none');
      });
      map.getStyle()?.layers?.forEach(l => {
        if (l.type === 'symbol')
          map.setLayoutProperty(l.id, 'visibility', layers.labels ? 'visible' : 'none');
      });
    };
    if (map.loaded()) apply(); else map.once('load', apply);
  }, [layers]);

  // ── Overlay visibility toggles ────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoadedRef.current) return;
    const vis = (v: boolean) => v ? 'visible' : 'none';
    const set = (id: string, v: boolean) => {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', vis(v));
    };
    set('inverters-fill',    showInverters);
    set('inverters-outline', showInverters);
    set('inverters-label',   showInverters);
    set('dc-cables-line',    showDcCables);
    set('ac-cables-line',    showAcCables);
  }, [showInverters, showDcCables, showAcCables]);

  // ── Render layout result ──────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !response || !siteBox || !mapLoadedRef.current) return;
    const [swLng, swLat] = siteBox;
    const g = response.geometry;
    const geom = g as any; // access optional new-engine fields safely

    // Tracker field extent — shown at low zoom as a subtle outline only
    getSource(map, 'tracker-field')?.setData({
      type: 'FeatureCollection',
      features: [rectToPolygon(
        g.tracker_field.x0, g.tracker_field.y0,
        g.tracker_field.x1, g.tracker_field.y1,
        swLng, swLat,
      )],
    });

    // ── 2D cell mask (valid_cells_mask[ri][bi] = 0|1) ──────────────────────
    const mask2d  = (geom.valid_cells_mask ?? []) as number[][];
    const hasMask = mask2d.length > 0;

    // Helper: is cell (ri, bi) valid?
    const cellOk = (ri: number, bi: number): boolean => {
      if (hasMask) return mask2d[ri]?.[bi] === 1;
      return bi < ((geom.valid_blocks_per_row as number[])?.[ri] ?? (geom.n_blocks as number));
    };

    // ── Individual tracker cells ─────────────────────────────────────────────
    const cellFeatures: GeoJSON.Feature[] = [];
    const nRows   = (geom.row_x0_list?.length   ?? 0) as number;
    const nBlocks = (geom.block_y0_list?.length ?? 0) as number;

    if (nRows > 0 && nBlocks > 0) {
      for (let ri = 0; ri < nRows; ri++) {
        const rx0 = geom.row_x0_list[ri] as number;
        for (let bi = 0; bi < nBlocks; bi++) {
          if (!cellOk(ri, bi)) continue;
          cellFeatures.push(rectToPolygon(
            rx0, geom.block_y0_list[bi],
            rx0 + g.table_w, geom.block_y1_list[bi],
            swLng, swLat,
          ));
        }
      }
    } else {
      // Legacy fallback
      const roadW = 6;
      let rowX = g.tracker_field.x0;
      for (let r = 0; r < g.n_rows; r++) {
        let blockY = g.eff_tracker_y0;
        for (let b = 0; b < g.n_blocks; b++) {
          cellFeatures.push(rectToPolygon(rowX, blockY, rowX + g.table_w, blockY + g.block_h, swLng, swLat));
          blockY += g.block_h + roadW;
        }
        rowX += g.row_pitch;
      }
    }

    getSource(map, 'tracker-rows')?.setData({ type: 'FeatureCollection', features: cellFeatures });

    // BESS compound
    if (g.dc_bess) {
      getSource(map, 'bess')?.setData({
        type: 'FeatureCollection',
        features: [rectToPolygon(g.dc_bess.x0, g.dc_bess.y0, g.dc_bess.x1, g.dc_bess.y1, swLng, swLat)],
      });
    }

    // ── Intelligent road network ─────────────────────────────────────────────
    // Roads only rendered where they serve adjacent valid tracker cells.
    // Principle: no dead stubs, road area ≤ 3% of tracker field (IEC TR 63226).
    const rw = g.road_w ?? 6;

    // ── Compute bounding box of all valid cells ───────────────────────────────
    let cellMinX = Infinity, cellMaxX = -Infinity;
    let cellMinY = Infinity, cellMaxY = -Infinity;
    for (let ri = 0; ri < nRows; ri++) {
      for (let bi = 0; bi < nBlocks; bi++) {
        if (!cellOk(ri, bi)) continue;
        const rx0 = geom.row_x0_list[ri] as number;
        cellMinX = Math.min(cellMinX, rx0);
        cellMaxX = Math.max(cellMaxX, rx0 + g.table_w);
        cellMinY = Math.min(cellMinY, geom.block_y0_list[bi] as number);
        cellMaxY = Math.max(cellMaxY, geom.block_y1_list[bi] as number);
      }
    }

    if (cellMinX === Infinity) {
      // No valid cells at all — clear roads and bail
      getSource(map, 'roads')?.setData({ type: 'FeatureCollection', features: [] });
    } else {
      // ── E-W cross roads — clipped x-extent to adjacent valid cells only ──
      const crossFeatures: GeoJSON.Feature[] = [];
      for (let bi = 0; bi < nBlocks - 1; bi++) {
        let minX = Infinity, maxX = -Infinity;
        for (let ri = 0; ri < nRows; ri++) {
          if (cellOk(ri, bi) || cellOk(ri, bi + 1)) {
            const rx0 = geom.row_x0_list[ri] as number;
            minX = Math.min(minX, rx0);
            maxX = Math.max(maxX, rx0 + g.table_w);
          }
        }
        if (minX === Infinity) continue; // no adjacent valid cells — omit road
        crossFeatures.push(rectToPolygon(
          minX, geom.block_y1_list[bi] as number,
          maxX, geom.block_y0_list[bi + 1] as number,
          swLng, swLat,
        ));
      }

      // ── N-S spine roads — clipped y-extent to adjacent valid cells only ──
      const spineFeatures: GeoJSON.Feature[] = [];
      (geom.spine_road_x0_list ?? []).forEach((sx: number) => {
        let minY = Infinity, maxY = -Infinity;
        for (let ri = 0; ri < nRows; ri++) {
          const rx0 = geom.row_x0_list[ri] as number;
          const rx1 = rx0 + g.table_w;
          // Is this row immediately adjacent to this spine road (west or east)?
          const isWest = rx1 <= sx + rw + 0.5;
          const isEast = rx0 >= sx - 0.5;
          if (!isWest && !isEast) return;
          for (let bi = 0; bi < nBlocks; bi++) {
            if (!cellOk(ri, bi)) continue;
            minY = Math.min(minY, geom.block_y0_list[bi] as number);
            maxY = Math.max(maxY, geom.block_y1_list[bi] as number);
          }
        }
        if (minY === Infinity) return; // no adjacent valid cells — omit spine road
        spineFeatures.push(rectToPolygon(sx, minY, sx + rw, maxY, swLng, swLat));
      });

      // ── Perimeter roads — follow actual valid-cell extents, not bbox ──────
      // Extend road_w outward from the valid-cell bounding box on each side.
      const px0 = cellMinX - rw;
      const px1 = cellMaxX + rw;
      const py0 = cellMinY - rw;
      const py1 = cellMaxY + rw;

      const perimFeatures: GeoJSON.Feature[] = [
        rectToPolygon(px0, py0,      px1, py0 + rw, swLng, swLat), // south
        rectToPolygon(px0, py1 - rw, px1, py1,      swLng, swLat), // north
        rectToPolygon(px0, py0,      px0 + rw, py1, swLng, swLat), // west
        rectToPolygon(px1 - rw, py0, px1, py1,      swLng, swLat), // east
      ];

      getSource(map, 'roads')?.setData({
        type: 'FeatureCollection',
        features: [...perimFeatures, ...spineFeatures, ...crossFeatures],
      });
    }

    // ── Inverter footprints ─────────────────────────────────────────────────
    const invFeatures: GeoJSON.Feature[] = (geom.inverter_placements ?? []).map((iv: any) => ({
      ...rectToPolygon(iv.x0, iv.y0, iv.x1, iv.y1, swLng, swLat),
      properties: { label: iv.label, power_kw: iv.power_kw, inv_type: iv.inv_type },
    }));
    getSource(map, 'inverters')?.setData({ type: 'FeatureCollection', features: invFeatures });

    // ── Cable segments ──────────────────────────────────────────────────────
    const cables = response.cables;
    if (cables) {
      const segToLine = (seg: any): GeoJSON.Feature<GeoJSON.LineString> => ({
        type: 'Feature',
        properties: { cable_type: seg.cable_type },
        geometry: {
          type: 'LineString',
          coordinates: [
            metresToLngLat(seg.x0, seg.y0, swLng, swLat),
            metresToLngLat(seg.x1, seg.y1, swLng, swLat),
          ],
        },
      });
      getSource(map, 'dc-cables')?.setData({
        type: 'FeatureCollection',
        features: (cables.dc_homerun_segments ?? []).map(segToLine),
      });
      getSource(map, 'ac-cables')?.setData({
        type: 'FeatureCollection',
        features: (cables.ac_mv_segments ?? []).map(segToLine),
      });
    }

    // Fit map to tracker field
    const cosLat = Math.cos(swLat * Math.PI / 180);
    const toLL   = (x: number, y: number): [number, number] => [
      swLng + x / (111_320 * cosLat),
      swLat + y / 110_540,
    ];
    map.fitBounds(
      [toLL(g.tracker_field.x0, g.tracker_field.y0),
       toLL(g.tracker_field.x1, g.tracker_field.y1)],
      { padding: 60, duration: 800 },
    );
  }, [response, siteBox]);

  // ── Import helpers ────────────────────────────────────────────────────────
  const applyImportedFC = (fc: GeoJSON.FeatureCollection) => {
    const map = mapRef.current;
    if (!map) return;
    getSource(map, 'site-draw')?.setData(fc);
    const box = bboxFromFC(fc);
    onSiteDrawnRef.current(box);
    map.fitBounds([[box[0], box[1]], [box[2], box[3]]], { padding: 60, duration: 1000 });
  };

  const handleShapefileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImportLoading(true); setImportError(null);
    try {
      const buf = await file.arrayBuffer();
      const geojson = await shp(buf);
      const fc: GeoJSON.FeatureCollection = Array.isArray(geojson)
        ? { type: 'FeatureCollection', features: geojson.flatMap(g => g.features) }
        : geojson;
      applyImportedFC(fc);
    } catch (err: any) {
      setImportError(`Shapefile import failed: ${err?.message || 'unknown error'}`);
    } finally {
      setImportLoading(false);
      if (shpInputRef.current) shpInputRef.current.value = '';
    }
  };

  const handleKMLUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImportLoading(true); setImportError(null);
    try {
      const text = await file.text();
      const dom  = new DOMParser().parseFromString(text, 'text/xml');
      const fc   = toGeoJSON.kml(dom) as GeoJSON.FeatureCollection;
      if (!fc?.features?.length) throw new Error('No features found in KML');
      applyImportedFC(fc);
    } catch (err: any) {
      setImportError(`KML import failed: ${err?.message || 'unknown error'}`);
    } finally {
      setImportLoading(false);
      if (kmlInputRef.current) kmlInputRef.current.value = '';
    }
  };

  const handleDXFUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImportLoading(true); setImportError(null);
    try {
      let DxfParser: any;
      try {
        DxfParser = require('dxf-parser');
        if (DxfParser.default) DxfParser = DxfParser.default;
      } catch { throw new Error('DXF parser unavailable — import a Shapefile or KML instead.'); }
      const text = await file.text();
      const dxf  = new DxfParser().parseSync(text);
      if (!dxf) throw new Error('Could not parse DXF file');
      const features: GeoJSON.Feature[] = [];
      for (const entity of dxf.entities || []) {
        if (entity.type === 'LWPOLYLINE' && (entity.vertices?.length ?? 0) >= 3) {
          const ring = (entity.vertices as any[]).map((v: any) => [v.x, v.y] as [number, number]);
          if (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1]) ring.push(ring[0]);
          features.push({ type: 'Feature', properties: { layer: entity.layer }, geometry: { type: 'Polygon', coordinates: [ring] } });
        }
      }
      if (!features.length) throw new Error('No LWPOLYLINE polygon entities found in DXF');
      applyImportedFC({ type: 'FeatureCollection', features });
    } catch (err: any) {
      setImportError(`DXF import failed: ${err?.message || 'unknown error'}`);
    } finally {
      setImportLoading(false);
      if (dxfInputRef.current) dxfInputRef.current.value = '';
    }
  };

  // ── Instruction text ──────────────────────────────────────────────────────
  const instrText = !isDrawing
    ? drawMode === 'exclusion'
      ? `✦  Click to start ${ZONE_LABELS[zoneType]} zone`
      : siteBox
      ? '✦  Click to redraw site boundary'
      : '✦  Click to start drawing site boundary  —  or import a file'
    : vertexCount < 3
    ? `✦  ${vertexCount} point${vertexCount === 1 ? '' : 's'} — keep clicking to add vertices`
    : '✦  Click first point to close  —  double-click  —  Esc cancels';

  if (!TOKEN) {
    return (
      <div style={noTokenStyle}>
        <p style={{ color: '#fff', textAlign: 'center', padding: 20 }}>
          <strong>REACT_APP_MAPBOX_TOKEN</strong> is not set.
        </p>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />

      <div style={instrStyle}>{instrText}</div>

      <div style={toolbarStyle}>

        <div style={toolGroupStyle}>
          <div style={toolGroupLabel}>IMPORT</div>
          <button style={toolBtnStyle} disabled={importLoading} onClick={() => shpInputRef.current?.click()}>📦 Shapefile (.zip)</button>
          <button style={toolBtnStyle} disabled={importLoading} onClick={() => kmlInputRef.current?.click()}>🌐 KML / KMZ</button>
          <button style={toolBtnStyle} disabled={importLoading} onClick={() => dxfInputRef.current?.click()}>📐 DXF</button>
        </div>

        <div style={toolGroupStyle}>
          <div style={toolGroupLabel}>BOUNDARY</div>
          {isDrawing && drawMode === 'site'
            ? <button style={{ ...toolBtnStyle, color: '#F39C12' }} onClick={cancelDrawing}>✕ Cancel ({vertexCount} pts)</button>
            : siteBox && !isDrawing
            ? <button style={toolBtnStyle} onClick={() => {
                cancelDrawing(); onClearSiteRef.current();
                const map = mapRef.current;
                if (map) getSource(map, 'site-draw')?.setData(emptyFC());
              }}>🗑 Clear site</button>
            : <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>Click map to begin</div>
          }
        </div>

        {/* ── Measure tool ────────────────────────────────────────────────── */}
        <div style={toolGroupStyle}>
          <div style={toolGroupLabel}>MEASURE</div>
          <button
            style={{
              ...toolBtnStyle,
              background:  measureMode ? 'rgba(245,158,11,0.85)' : toolBtnStyle.background as string,
              borderColor: measureMode ? '#f59e0b'               : 'rgba(255,255,255,0.15)',
            }}
            onClick={() => {
              const entering = !measureMode;
              setMeasureMode(entering);
              measureModeRef.current = entering;
              if (entering) {
                cancelDrawing(); // exit draw mode when entering measure
              } else {
                // Exiting measure — clear all measure geometry
                measurePointsRef.current = [];
                measureClosedRef.current = false;
                setMeasurePoints([]);
                setMeasureClosed(false);
                const m = mapRef.current;
                if (m) _updateMeasureOverlay(m, [], false);
              }
            }}
          >
            📏 {measureMode ? 'Exit measure' : 'Measure'}
          </button>

          {measureMode && measurePoints.length === 0 && (
            <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10, lineHeight: 1.5 }}>
              Click to place points · Dbl-click to close · Esc to clear
            </div>
          )}

          {measureMode && measurePoints.length >= 2 && (
            <button
              style={{ ...toolBtnStyle, fontSize: 11 }}
              onClick={() => {
                measurePointsRef.current = [];
                measureClosedRef.current = false;
                setMeasurePoints([]);
                setMeasureClosed(false);
                const m = mapRef.current;
                if (m) _updateMeasureOverlay(m, [], false);
              }}
            >
              🗑 Clear measurement
            </button>
          )}

          {measureMode && measurePoints.length >= 2 && !measureClosed && (
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10 }}>
              {measurePoints.length} pts — dbl-click to close &amp; get area
            </div>
          )}
        </div>

        <div style={toolGroupStyle}>
          <div style={toolGroupLabel}>EXCLUSION ZONES</div>
          <select value={zoneType} onChange={e => setZoneType(e.target.value as ExclusionZoneType)} style={selectStyle}>
            {(Object.entries(ZONE_LABELS) as [ExclusionZoneType, string][]).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <button
            style={{
              ...toolBtnStyle,
              background: drawMode === 'exclusion' ? `${ZONE_COLORS[zoneType]}cc` : toolBtnStyle.background as string,
              borderColor: drawMode === 'exclusion' ? ZONE_COLORS[zoneType] : 'rgba(255,255,255,0.15)',
            }}
            onClick={() => {
              cancelDrawing();
              setDrawMode(m => m === 'exclusion' ? 'site' : 'exclusion');
            }}
          >
            {drawMode === 'exclusion' ? (isDrawing ? `✕ Cancel (${vertexCount} pts)` : '✕ Exit zone mode') : '✏️ Draw Zone'}
          </button>
          {exclusionZones.length > 0 && (
            <button style={{ ...toolBtnStyle, fontSize: 11 }} onClick={() => {
              onExclusionZoneClear();
              const map = mapRef.current;
              if (map) getSource(map, 'exclusion-zones')?.setData(emptyFC());
            }}>🗑 Clear ({exclusionZones.length})</button>
          )}
        </div>

        <div style={toolGroupStyle}>
          <button
            style={{ ...toolBtnStyle, background: layerPanel ? 'rgba(46,95,163,0.9)' : toolBtnStyle.background as string }}
            onClick={() => setLayerPanel(p => !p)}
          >
            🗂 Layers
          </button>
          {layerPanel && (
            <div style={{ paddingTop: 4 }}>
              {(Object.entries(layers) as [keyof LayerToggles, boolean][]).map(([key, val]) => (
                <label key={key} style={layerRowStyle}>
                  <input type="checkbox" checked={val} onChange={() => setLayers(p => ({ ...p, [key]: !p[key] }))} style={{ marginRight: 8 }} />
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Overlays: inverters + cables */}
        <div style={toolGroupStyle}>
          <div style={toolGroupLabel}>OVERLAYS</div>
          <label style={layerRowStyle}>
            <input type="checkbox" checked={showInverters} onChange={() => setShowInverters(p => !p)} style={{ marginRight: 8, accentColor: '#C55A11' }} />
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: '#C55A11', flexShrink: 0, display: 'inline-block' }} />
              Inverters
            </span>
          </label>
          <label style={layerRowStyle}>
            <input type="checkbox" checked={showDcCables} onChange={() => setShowDcCables(p => !p)} style={{ marginRight: 8, accentColor: '#f97316' }} />
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 10, height: 3, background: '#f97316', flexShrink: 0, display: 'inline-block' }} />
              DC Home-run
            </span>
          </label>
          <label style={layerRowStyle}>
            <input type="checkbox" checked={showAcCables} onChange={() => setShowAcCables(p => !p)} style={{ marginRight: 8, accentColor: '#dc2626' }} />
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 10, height: 3, background: '#dc2626', flexShrink: 0, display: 'inline-block' }} />
              AC MV Cable
            </span>
          </label>
        </div>

        {exclusionZones.length > 0 && (
          <div style={legendStyle}>
            <div style={toolGroupLabel}>ZONES ON MAP</div>
            {Array.from(new Set(exclusionZones.map(z => z.zone_type))).map(zt => (
              <div key={zt} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0' }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: ZONE_COLORS[zt], flexShrink: 0 }} />
                <span style={{ color: '#fff', fontSize: 11 }}>
                  {ZONE_LABELS[zt]} ({exclusionZones.filter(z => z.zone_type === zt).length})
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <input ref={shpInputRef} type="file" accept=".zip"      style={{ display: 'none' }} onChange={handleShapefileUpload} />
      <input ref={kmlInputRef} type="file" accept=".kml,.kmz" style={{ display: 'none' }} onChange={handleKMLUpload} />
      <input ref={dxfInputRef} type="file" accept=".dxf"      style={{ display: 'none' }} onChange={handleDXFUpload} />

      {importLoading && <div style={loadingOverlayStyle}>⏳ Importing…</div>}
      {importError && (
        <div style={errorToastStyle}>
          ⚠️ {importError}
          <button onClick={() => setImportError(null)} style={{ marginLeft: 10, background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}>✕</button>
        </div>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const noTokenStyle: React.CSSProperties = { width: '100%', height: '100%', background: '#1F3864', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const instrStyle: React.CSSProperties = { position: 'absolute', bottom: 40, left: '50%', transform: 'translateX(-50%)', background: 'rgba(31,56,100,0.85)', color: '#fff', padding: '8px 16px', borderRadius: 20, fontSize: 13, fontWeight: 500, pointerEvents: 'none', whiteSpace: 'nowrap', zIndex: 1000 };
const toolbarStyle: React.CSSProperties = { position: 'absolute', top: 12, left: 12, display: 'flex', flexDirection: 'column', gap: 8, zIndex: 1000, maxWidth: 210 };
const toolGroupStyle: React.CSSProperties = { background: 'rgba(14,22,38,0.88)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.10)', padding: '8px 10px', backdropFilter: 'blur(8px)', display: 'flex', flexDirection: 'column', gap: 4 };
const toolGroupLabel: React.CSSProperties = { color: 'rgba(255,255,255,0.4)', fontSize: 9, fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase' as const, marginBottom: 2 };
const toolBtnStyle: React.CSSProperties = { background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '6px 10px', fontSize: 12, fontWeight: 500, cursor: 'pointer', textAlign: 'left' as const, whiteSpace: 'nowrap' as const };
const selectStyle: React.CSSProperties = { background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '5px 8px', fontSize: 12, cursor: 'pointer', width: '100%' };
const layerRowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', color: '#fff', fontSize: 12, padding: '2px 0', cursor: 'pointer' };
const legendStyle: React.CSSProperties = { background: 'rgba(14,22,38,0.88)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.10)', padding: '8px 10px', backdropFilter: 'blur(8px)' };
const loadingOverlayStyle: React.CSSProperties = { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', background: 'rgba(14,22,38,0.92)', color: '#fff', padding: '12px 24px', borderRadius: 10, fontSize: 14, fontWeight: 500, zIndex: 1000 };
const errorToastStyle: React.CSSProperties = { position: 'absolute', bottom: 80, left: '50%', transform: 'translateX(-50%)', background: 'rgba(180,40,40,0.92)', color: '#fff', padding: '8px 16px', borderRadius: 8, fontSize: 13, display: 'flex', alignItems: 'center', zIndex: 1000, whiteSpace: 'nowrap' as const };
